import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

/** Pictos dédiés à la démo — pas les cartes du vrai Memory. */
const DEMO_FACES = [
  { shape: 'circle', color: '#264653' },
  { shape: 'square', color: '#2a9d8f' },
  { shape: 'triangle', color: '#e9c46a' },
  { shape: 'diamond', color: '#f4a261' },
  { shape: 'star', color: '#e76f51' },
  { shape: 'hex', color: '#457b9d' },
  { shape: 'heart', color: '#9b5de5' },
  { shape: 'plus', color: '#00bbf9' },
  { shape: 'moon', color: '#00f5d4' },
  { shape: 'leaf', color: '#fee440' },
  { shape: 'ring', color: '#f15bb5' },
  { shape: 'bolt', color: '#7bdff2' },
];

const PAIR_COUNT = 12;
const COLS = 6;
const ROWS = 4;

function PictoFace({ face }) {
  const color = face.color || '#f76f4b';
  const common = { fill: color };
  return (
    <svg viewBox="0 0 64 64" className="w-[62%] h-[62%]" aria-hidden>
      {face.shape === 'circle' && <circle cx="32" cy="32" r="20" {...common} />}
      {face.shape === 'square' && <rect x="12" y="12" width="40" height="40" rx="6" {...common} />}
      {face.shape === 'triangle' && <polygon points="32,10 54,52 10,52" {...common} />}
      {face.shape === 'diamond' && <polygon points="32,8 56,32 32,56 8,32" {...common} />}
      {face.shape === 'star' && <polygon points="32,8 39,24 56,26 43,38 47,54 32,45 17,54 21,38 8,26 25,24" {...common} />}
      {face.shape === 'hex' && <polygon points="32,8 52,19 52,45 32,56 12,45 12,19" {...common} />}
      {face.shape === 'heart' && <path d="M32 54C18 42 8 32 8 22a12 12 0 0 1 24 0 12 12 0 0 1 24 0c0 10-10 20-24 32z" {...common} />}
      {face.shape === 'plus' && <path d="M26 10h12v16h16v12H38v16H26V38H10V26h16z" {...common} />}
      {face.shape === 'moon' && <path d="M40 10a22 22 0 1 0 10 38 20 20 0 1 1-10-38z" {...common} />}
      {face.shape === 'leaf' && <path d="M48 12C28 14 14 28 14 46c18 0 32-14 34-34z" {...common} />}
      {face.shape === 'ring' && <circle cx="32" cy="32" r="18" fill="none" stroke={color} strokeWidth="8" />}
      {face.shape === 'bolt' && <polygon points="36,8 18,36 30,36 26,56 48,26 34,26" {...common} />}
    </svg>
  );
}

function DemoMouse({ leftPressed = false }) {
  return (
    <div className="flex flex-col items-center gap-1.5" aria-hidden>
      <svg viewBox="0 0 72 110" className="w-[6.375rem] h-[9.75rem] drop-shadow-md" role="img">
        <title>Souris</title>
        <rect x="8" y="38" width="56" height="64" rx="28" fill="#e8eaed" stroke="#9aa0a6" strokeWidth="2" />
        <path
          d="M10 38 h24 v28 a4 4 0 0 1 -4 4 H14 a4 4 0 0 1 -4 -4 Z"
          fill={leftPressed ? '#f76f4b' : '#ffffff'}
          stroke="#9aa0a6"
          strokeWidth="2"
        />
        <path
          d="M38 38 h24 v28 a4 4 0 0 1 -4 4 H42 a4 4 0 0 1 -4 -4 Z"
          fill="#ffffff"
          stroke="#9aa0a6"
          strokeWidth="2"
        />
        <rect x="31" y="44" width="10" height="18" rx="5" fill="#bdc1c6" stroke="#9aa0a6" strokeWidth="1.5" />
        <line x1="36" y1="38" x2="36" y2="68" stroke="#9aa0a6" strokeWidth="2" />
      </svg>
      <p className="text-xs text-gray-400 text-center leading-tight max-w-[7rem]">
        {leftPressed ? 'Clic gauche' : 'Souris'}
      </p>
    </div>
  );
}

function buildDemoCards() {
  // Disposition fixe pour la démo (reproductible) — 12 paires / 24 cartes
  const order = [
    0, 3, 1, 7, 2, 5,
    4, 0, 8, 6, 1, 9,
    10, 2, 11, 4, 3, 8,
    5, 9, 6, 10, 7, 11,
  ];
  return order.map((pair, index) => ({
    id: `d${index}`,
    pair,
    face: DEMO_FACES[pair],
  }));
}

/** Séquences : mismatch / match / mismatch / match */
const SEQUENCES = [
  { a: 0, b: 1, match: false }, // pair 0 vs 3
  { a: 0, b: 7, match: true }, // pair 0 + 0
  { a: 2, b: 3, match: false }, // pair 1 vs 7
  { a: 2, b: 10, match: true }, // pair 1 + 1
];

/**
 * Démo Memory 12 paires (cartes ≠ jeu réel).
 * Boucle sur 4 coups (2 échecs + 2 réussites) jusqu'à Fermer.
 */
export default function MemoryIntroDemo({ onClose, elapsedLabel = '00:00' }) {
  const cards = useMemo(() => buildDemoCards(), []);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [mouseLeft, setMouseLeft] = useState(false);
  const wrapRef = useRef(null);
  const [pane, setPane] = useState({ w: 0, h: 0 });
  const cancelledRef = useRef(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => {
      const dialog = el.closest('[data-game-season-dialog]');
      const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
      const bottom = scroller
        ? scroller.getBoundingClientRect().bottom
        : window.innerHeight - 16;
      const top = el.getBoundingClientRect().top;
      setPane({
        w: Math.max(160, el.clientWidth),
        h: Math.max(140, bottom - top - 16),
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    const timers = [];
    const wait = (ms) => new Promise((resolve) => {
      const id = window.setTimeout(resolve, ms);
      timers.push(id);
    });

    const resetBoard = () => {
      setFlipped([]);
      setMatched([]);
      setMoves(0);
      setMouseLeft(false);
    };

    const clickLeft = async () => {
      setMouseLeft(true);
      await wait(180);
      setMouseLeft(false);
      await wait(60);
    };

    const runSequence = async (seq) => {
      if (cancelledRef.current) return;
      await clickLeft();
      if (cancelledRef.current) return;
      setFlipped([cards[seq.a].id]);
      await wait(450);
      if (cancelledRef.current) return;
      await clickLeft();
      if (cancelledRef.current) return;
      setFlipped([cards[seq.a].id, cards[seq.b].id]);
      setMoves((m) => m + 1);
      await wait(900);
      if (cancelledRef.current) return;
      if (seq.match) {
        const pair = cards[seq.a].pair;
        setMatched((prev) => (prev.includes(pair) ? prev : [...prev, pair]));
        setFlipped([]);
        await wait(500);
      } else {
        setFlipped([]);
        await wait(450);
      }
    };

    const loopOnce = async () => {
      resetBoard();
      await wait(600);
      for (const seq of SEQUENCES) {
        if (cancelledRef.current) return;
        await runSequence(seq);
      }
      await wait(1400);
    };

    const run = async () => {
      await wait(200);
      while (!cancelledRef.current) {
        await loopOnce();
      }
    };
    run();

    return () => {
      cancelledRef.current = true;
      timers.forEach((id) => window.clearTimeout(id));
      setMouseLeft(false);
    };
  }, [cards]);

  const open = new Set([
    ...flipped,
    ...cards.filter((c) => matched.includes(c.pair)).map((c) => c.id),
  ]);

  const gap = 6;
  const statsH = 48;
  const availH = Math.max(120, pane.h - statsH);
  // Cartes sur les 2/3 de la largeur, souris sur le 1/3 restant
  const twoThirdsW = Math.max(160, Math.floor(pane.w * (2 / 3)));
  const cardFromW = twoThirdsW ? (twoThirdsW - gap * (COLS - 1)) / COLS : 64;
  const cardFromH = (availH - gap * (ROWS - 1)) / ROWS;
  const cardPx = Math.max(36, Math.floor(Math.min(cardFromW, cardFromH, 110)));
  const gridW = cardPx * COLS + gap * (COLS - 1);
  const qSize = Math.max(12, Math.round(cardPx * 0.32));

  return (
    <div
      className="relative rounded-md border border-orange-300/60 dark:border-orange-800/60 bg-white/95 dark:bg-gray-950/95 p-3"
      role="dialog"
      aria-label="Démonstration du Memory"
    >
      <div className="relative flex items-center justify-center mb-2 min-h-[2rem]">
        <p className="text-lg font-semibold uppercase tracking-wide text-[#f76f4b]">Démo</p>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 shrink-0 rounded-md bg-[#f76f4b] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#e85f3a]"
        >
          <X size={14} />
          Fermer
        </button>
      </div>

      <p className="text-sm font-mono font-bold text-[#f76f4b] mb-1 tabular-nums">{elapsedLabel}</p>
      <p className="text-xs text-gray-500 mb-2">
        {matched.length} / {PAIR_COUNT} paires · {moves} coup{moves !== 1 ? 's' : ''}
      </p>

      <div
        ref={wrapRef}
        className="w-full min-w-0 pointer-events-none select-none grid grid-cols-1 sm:grid-cols-3 gap-3 items-stretch"
      >
        <div className="sm:col-span-2 flex items-center justify-center min-w-0">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${COLS}, ${cardPx}px)`,
              width: gridW,
              gap,
            }}
          >
            {cards.map((card) => {
              const isOpen = open.has(card.id);
              const isMatch = matched.includes(card.pair);
              return (
                <div
                  key={card.id}
                  className={`rounded-xl overflow-hidden border-2 flex items-center justify-center transition-colors ${
                    isMatch
                      ? 'border-emerald-400 bg-white'
                      : isOpen
                        ? 'border-[#f76f4b] bg-white'
                        : 'border-[#f76f4b]/40 bg-[#111827]'
                  }`}
                  style={{ width: cardPx, height: cardPx }}
                >
                  {isOpen ? (
                    <PictoFace face={card.face} />
                  ) : (
                    <span
                      className="font-black text-[#f76f4b]/90 select-none leading-none"
                      style={{ fontSize: qSize }}
                    >
                      ?
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="sm:col-span-1 flex items-center justify-center min-w-0">
          <DemoMouse leftPressed={mouseLeft} />
        </div>
      </div>
    </div>
  );
}
